import { StateCreator } from "zustand";

import { PassportFieldName } from "../../models/documents/international-passport";
import { SlicedStore } from "../store";

export interface PassportFormValues {
  [PassportFieldName.givenName]: string;
  [PassportFieldName.surname]: string;
  [PassportFieldName.countryCode]: string;
  [PassportFieldName.nationality]: string;
  [PassportFieldName.number]: string;
  [PassportFieldName.gender]: string;
  [PassportFieldName.birthdate]: string;
  [PassportFieldName.expiryDate]: string;
  [PassportFieldName.passportType]: string;
}

export interface PassportFormErrors {
  [PassportFieldName.givenName]: string | null;
  [PassportFieldName.surname]: string | null;
  [PassportFieldName.countryCode]: string | null;
  [PassportFieldName.nationality]: string | null;
  [PassportFieldName.number]: string | null;
  [PassportFieldName.gender]: string | null;
  [PassportFieldName.birthdate]: string | null;
  [PassportFieldName.expiryDate]: string | null;
  [PassportFieldName.passportType]: string | null;
}

const EMPTY_PASSPORT: PassportFormValues = {
  [PassportFieldName.givenName]: "",
  [PassportFieldName.surname]: "",
  [PassportFieldName.countryCode]: "",
  [PassportFieldName.nationality]: "",
  [PassportFieldName.number]: "",
  [PassportFieldName.gender]: "",
  [PassportFieldName.birthdate]: "",
  [PassportFieldName.expiryDate]: "",
  [PassportFieldName.passportType]: "",
};

const EMPTY_ERRORS: PassportFormErrors = {
  [PassportFieldName.givenName]: null,
  [PassportFieldName.surname]: null,
  [PassportFieldName.countryCode]: null,
  [PassportFieldName.nationality]: null,
  [PassportFieldName.number]: null,
  [PassportFieldName.gender]: null,
  [PassportFieldName.birthdate]: null,
  [PassportFieldName.expiryDate]: null,
  [PassportFieldName.passportType]: null,
};

export interface PassportSlice {
  hasSignalsMismatch: boolean;
  setHasSignalsMismatch: (hasMismatch: boolean) => void;

  hasManualBookingMismatch: boolean;
  setHasManualBookingMismatch: (hasMismatch: boolean) => void;
  passportValues: PassportFormValues;
  passportErrors: PassportFormErrors;
  // The paxSessionId the current passport form values belong to. Lets us detect
  // a passenger switch and clear the form even across remounts of the passport
  // page (the form state is a global slice, shared across passengers).
  passportValuesPaxSessionId: string | undefined;
  changePassportValue: (fieldName: string) => (value: string) => void;
  setPassportError: (fieldName: string, error: string | null) => void;
  resetPassportErrors: () => void;
  resetPassportValues: (paxSessionId?: string) => void;
}

export const createPassportSlice: StateCreator<
  SlicedStore,
  [],
  [],
  PassportSlice
> = (set, get) => ({
  hasSignalsMismatch: false,
  setHasSignalsMismatch: (hasMismatch: boolean) => {
    set(() => ({ hasSignalsMismatch: hasMismatch }));
  },
  hasManualBookingMismatch: false,
  setHasManualBookingMismatch: (hasMismatch: boolean) => {
    set(() => ({ hasManualBookingMismatch: hasMismatch }));
  },
  passportValues: EMPTY_PASSPORT,
  passportErrors: EMPTY_ERRORS,
  passportValuesPaxSessionId: undefined,
  changePassportValue: (fieldName: string) => (value: string) => {
    set((state) => ({
      ...state,
      passportValues: {
        ...state.passportValues,
        [fieldName]: value,
      },
      passportErrors: {
        ...state.passportErrors,
        [fieldName]: null,
      },
      hasSignalsMismatch: false,
    }));
  },
  setPassportError: (fieldName: string, error: string | null) => {
    set((state) => ({
      ...state,
      passportErrors: {
        ...state.passportErrors,
        [fieldName]: error,
      },
    }));
  },
  resetPassportErrors: () => {
    set((state) => ({
      ...state,
      passportErrors: EMPTY_ERRORS,
    }));
  },
  // Clears the manual passport form (values + errors) and records which
  // passenger the (now empty) form belongs to. Used when switching passengers so
  // one passenger's entered/prefilled data doesn't leak into the next
  // passenger's form and preview. Also clears the mismatch flags, which live in
  // the same slice and would otherwise leak the previous passenger's state (e.g.
  // auto-opening the manual form on the next passenger).
  resetPassportValues: (paxSessionId?: string) => {
    set((state) => ({
      ...state,
      passportValues: EMPTY_PASSPORT,
      passportErrors: EMPTY_ERRORS,
      passportValuesPaxSessionId: paxSessionId,
      hasSignalsMismatch: false,
      hasManualBookingMismatch: false,
    }));
  },
});
